import { Link, useInRouterContext } from "react-router-dom";
import { PILL_SOLID, type PillTone } from "./StatusPill";
import { accountStatusLabel, useAccountStatus, type AccountStatusDto } from "../lib/accountStatus";

// THE ACCOUNT SYMBOL (Session SC9B, the Director's design of 22 October 2026): one small pound sign beside a customer's name on an office
// screen, worked out from Xero every time it is shown.
//
//   RED    #c0392b  the account is ON STOP: an invoice more than 30 days past its due date.
//   GREEN  #63C41F  up to date (or in credit): nothing more than 30 days past due.
//   GREY            NOT CHECKED: the invoice read failed or is out of date. Never green on a check that did not run.
//
// Colour is never the only signal: the pound glyph is on every chip, and the accessible name and the hover line say the words ("On stop:
// GBP X more than 30 days overdue, oldest N days", "Account up to date", "Account not checked"). The chip's fill and ink are the
// StatusPill SOLID pairs (red with white ink, green and grey with the CTA-rule dark ink), so the contrast lint already measures them.
// It links to the customer's money, the Overview tab (balance, overdue and invoices).
//
// A FLAG, NEVER A BLOCK: nothing reads this to refuse an action. It is shown on OFFICE screens only; no engineer, customer-portal, RFID or
// public page imports it (accountOnStopNeverLeaks.test.ts). `plain` renders it without a link, for a place already inside a link.

interface Props {
  customerId: string | null | undefined;
  /** The status when the page's own payload already carries it (the register, the overview); otherwise the shared batched read is used. */
  status?: AccountStatusDto | null;
  /** No link: for a place already inside a link (a list row that is itself a link). */
  plain?: boolean;
  /** Hidden from assistive technology, for a place whose own accessible name must not change (a listbox option); the hover line stays. Implies plain. */
  decorative?: boolean;
  className?: string;
}

export function AccountSymbol({ customerId, status, plain: plainProp = false, decorative = false, className = "" }: Props) {
  // Outside a router there is nowhere to link to, so it reads as the plain symbol rather than throwing.
  const inRouter = useInRouterContext();
  const plain = plainProp || decorative || !inRouter;
  const s = useAccountStatus(customerId, status ?? null);
  if (!customerId || !s) return null;
  // red = on stop, green = up to date, grey = not checked (one ternary, so there is no second status map)
  const tone: PillTone = s.onStop === true ? "red" : s.onStop === false ? "green" : "grey";
  const label = accountStatusLabel(s);
  const solid = PILL_SOLID[tone];
  const chip = (
    <span
      aria-hidden
      data-testid="account-symbol-chip"
      className="inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full font-heading text-[0.7rem] font-bold leading-none"
      style={{ backgroundColor: solid.fill, color: solid.ink }}
    >
      £
    </span>
  );
  const common = {
    "data-account-status": String(s.onStop),
    "data-tone": tone,
    title: label,
    "aria-label": label,
  } as const;
  // The wrapper is a plain inline-block so the phone's 44px control floor (which keys on a link that is a flex item, or an inline-flex link)
  // does not stretch a name's line; the link inside keeps a comfortable padded target of its own.
  return (
    <span className={`mx-1 inline-block flex-none align-middle ${className}`.trim()} data-testid="account-symbol">
      {plain ? (
        <span {...(decorative ? { "aria-hidden": true, title: label, "data-account-status": String(s.onStop), "data-tone": tone } : { role: "img", ...common })} className="inline-block">
          {chip}
        </span>
      ) : (
        <Link to={`/office/customers/${encodeURIComponent(customerId)}?tab=overview`} {...common} className="relative inline-block p-1 max-sm:after:absolute max-sm:after:-inset-3 max-sm:after:content-['']">
          {chip}
        </Link>
      )}
    </span>
  );
}

/** The one warning wording a confirm carries (an entrapment or emergency intake always reads the attend-anyway sentence). */
export function onStopWarning(s: AccountStatusDto, urgent: boolean): string {
  return urgent
    ? "Account on stop. Attend anyway: an entrapment is always attended."
    : `Account on stop: invoices totalling GBP ${(s.overdueAmount ?? 0).toFixed(2)} more than 30 days overdue. You can carry on.`;
}

/**
 * THE ONE AMBER LINE a confirm carries when the customer's account is ON STOP (Session SC9B). A FLAG, NEVER A BLOCK: it is a line of words in the
 * confirm the person already sees, one press carries on, and nothing is refused or disabled by it. Shown only for a red account; a grey (not
 * checked) account adds nothing here, because the confirm must not nag on a check that did not run. `urgent` is an entrapment or emergency
 * intake, which always reads the attend-anyway wording. Office screens only (accountOnStopNeverLeaks.test.ts).
 */
export function AccountOnStopLine({ customerId, urgent = false, className = "" }: { customerId: string | null | undefined; urgent?: boolean; className?: string }) {
  const s = useAccountStatus(customerId, null);
  if (!customerId || !s || s.onStop !== true) return null;
  const text = onStopWarning(s, urgent);
  return (
    <p
      role="status"
      data-testid="account-on-stop-line"
      className={`mt-3 rounded-ele border border-ele-amber-line bg-ele-amber-wash px-3 py-2 text-sm text-ele-amber-wash-ink ${className}`.trim()}
    >
      {text}
    </p>
  );
}

/**
 * The same warning as a small note for the in-place ConfirmButton (its armedNote, which draws it in a portal so nothing can clip it): it appears under the armed button for a red account
 * and nowhere else. A FLAG, NEVER A BLOCK: it sits beside the confirm and never changes it.
 */
export function AccountOnStopNote({ customerId, urgent = false }: { customerId: string | null | undefined; urgent?: boolean }) {
  const s = useAccountStatus(customerId, null);
  if (!customerId || !s || s.onStop !== true) return null;
  const text = onStopWarning(s, urgent);
  return (
    <span
      role="status"
      data-testid="account-on-stop-note"
      className="block rounded-ele border border-ele-amber-line bg-ele-amber-wash px-2 py-1 text-xs font-normal normal-case text-ele-amber-wash-ink shadow"
    >
      {text}
    </span>
  );
}
